import axios from '../lib/axios';
import { showToast } from '../utils/toast';
import { Permission } from '../types/auth';

export interface LenderUser {
  _id: string;
  name: string;
  email?: string;
  mobileNo: string;
  code?: string;
  dashboard: boolean;
  dashPermissions: Permission[];
  permissions: string[];
  suspended: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  logs?: any[];
}


/**
 * Fetch all lender users
 */
export const fetchLenderUsers = async (token: string | null): Promise<LenderUser[]> => {
  try {
    const response = await axios.get('/lender-user/fetch', {
      headers: {
        'Content-Type': 'application/json',
        token: token || ''
      }
    });

    if (response.data?.status === "Request successful!" && Array.isArray(response.data.result?.data)) {
      return response.data.result.data;
    }

    throw new Error('Invalid response format');
  } catch (error) {
    console.error('Fetch lender users failed:', error);
    showToast.error('Failed to fetch lender users');
    throw error;
  }
};

/**
 * Fetch lender user by ID
 */
export const fetchLenderUserById = async (
  id: string,
  token: string | null
): Promise<LenderUser> => {
  try {
    const response = await axios.get(`/lender-user/fetch/${id}`, {
      headers: {
        'Content-Type': 'application/json',
        token: token || ''
      }
    });

    if (response.data?.status === "Request successful!" && response.data.result?.data) {
      return response.data.result.data;
    }

    throw new Error('Invalid response format');
  } catch (error) {
    console.error('Fetch lender user failed:', error);
    showToast.error('Failed to fetch lender user');
    throw error;
  }
};

/**
 * Create lender user
 */
export const createLenderUser = async (
  payload: Partial<LenderUser>,
  token: string | null
): Promise<LenderUser> => {
  try {
    const response = await axios.post('/lender-user/create', payload, {
      headers: {
        'Content-Type': 'application/json',
        token: token || ''
      }
    });

    if (response.status === 201 && response.data?.result?.user) {
      showToast.success('Lender user created successfully');
      return response.data.result.user;
    }

    throw new Error('Invalid response format');
  } catch (error: any) {
    console.error('Create lender user failed:', error);

    const message =
      error.response?.data?.message || 'Failed to create lender user';

    showToast.error(message);
    throw error;
  }
};

/**
 * Update lender user (ID-based only)
 */
export const updateLenderUser = async (
  id: string,
  payload: Partial<LenderUser>,
  token: string | null
): Promise<LenderUser> => {
  try {
    const response = await axios.put(`/lender-user/update/${id}`, payload, {
      headers: {
        'Content-Type': 'application/json',
        token: token || ''
      }
    });

    if (response.data?.status === "Request successful!" && response.data.result?.user) {
      showToast.success('Lender user updated successfully');
      return response.data.result.user;
    }

    throw new Error('Invalid response format');
  } catch (error: any) {
    console.error('Update lender user failed:', error);

    const message =
      error.response?.data?.message || 'Failed to update lender user';

    showToast.error(message);
    throw error;
  }
};

/**
 * Delete lender user (ID-based)
 */
export const deleteLenderUser = async (
  id: string,
  token: string | null
): Promise<boolean> => {
  try {
    const response = await axios.delete(`/lender-user/delete/${id}`, {
      headers: {
        'Content-Type': 'application/json',
        token: token || ''
      }
    });

    if (response.data?.status === "Request successful!") {
      showToast.success('Lender user deleted successfully');
      return true;
    }

    throw new Error('Delete failed');
  } catch (error: any) {
    console.error('Delete lender user failed:', error);

    const message =
      error.response?.data?.message || 'Failed to delete lender user';

    showToast.error(message);
    throw error;
  }
};